import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Form, notification, Checkbox } from 'antd';
import { useNavigate } from 'react-router-dom';
import { HOME_ROUTE } from "../../router/routePaths";
import { useAppDispatch } from "../../hooks";
import { registerUser, sendEmailCode } from "../../store/thunks/userThunks";
import {EMAIL_ACTIONS} from "../../config/GlobalConfig";
import log from 'loglevel';
import { debounce } from 'lodash';

interface FormValues {
    email: string;
    code: string;
    password: string;
    confirm: string;
}

interface SignupFormProps {
    onClose?: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onClose }) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const dispatch = useAppDispatch();

    const debouncedRegister = useRef(
        debounce(async (values: FormValues) => {
            try {
                const response = await dispatch(registerUser({
                    email: values.email,
                    password: values.password,
                    verificationCode: values.code,
                })).unwrap();
    
                if (response.code === 200) {
                    notification.success({
                        message: '注册成功'
                    });
                    onClose?.();
                } else {
                    notification.error({
                        message: response.message
                    });
                }
            } catch (error) {
                notification.error({
                    message: '输入有误'
                });
            } finally {
                setLoading(false);
            }
        }, 1000)
    ).current;
    

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            debouncedRegister.cancel();
        };
    }, []);

    const startCountdown = (): void => {
        setCountdown(60);
        timerRef.current = setInterval(() => {
            setCountdown((prevCount) => {
                if (prevCount <= 1) {
                    clearInterval(timerRef.current!);
                    return 0;
                }
                return prevCount - 1;
            });
        }, 1000);
    };

    const handleGetCode = async (): Promise<void> => {
        const email = form.getFieldValue('email');
        if (!email) {
            notification.error({
                message: '请先输入您的电子邮箱地址'
            });
            return;
        }

        setLoading(true);
        try {
            const response = await dispatch(sendEmailCode({email: email, action: EMAIL_ACTIONS.REGISTER})).unwrap();
            if (response.code === 200) {
                notification.success({
                    message: '验证码已发送到您的邮箱'
                });
                startCountdown();
            } else {
                notification.error({
                    message: response.message
                });
            }
        } catch (error) {
            log.debug(error)
            notification.error({
                message: '验证码发送失败'
            });
        } finally {
            setLoading(false);
        }
    };

    const onFinish = async (values: FormValues): Promise<void> => {
        setLoading(true);
        debouncedRegister(values);
    };

    return (
        <Form form={form} onFinish={onFinish}>
            <Form.Item
                name="email"
                rules={[{ type: 'email', message: '请输入有效的邮箱地址！' }, {
                    required: true, message: '请输入您的邮箱！'
                }]}
            >
                <Input placeholder="邮箱" style={{ height: '40px' }}/>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
                <Form.Item name="code"
                           rules={[{ min: 6, max: 6, required: true, message: '请输入6位邮箱验证码！' }]}
                           style={{ display: 'inline-block', width: 'calc(60% - 12px)' }}>
                    <Input placeholder="输入邮箱验证码" style={{ height: '40px' }}/>
                </Form.Item>
                <Button type="primary" style={{ display: 'inline-block', width: '40%', height: '40px', marginLeft: '12px' }}
                        loading={loading} onClick={handleGetCode}>
                    {countdown > 0 ? `${countdown}秒` : '获取验证码'}
                </Button>
            </Form.Item>
            <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入您的密码！' }, {
                    min: 8, max: 16, message: '密码长度必须在8到16个字符之间！'
                }]}
                hasFeedback
            >
                <Input.Password placeholder="密码" style={{ height: '40px' }}/>
            </Form.Item>
            <Form.Item
                name="confirm"
                dependencies={['password']}
                hasFeedback
                rules={[{ required: true, message: '请确认您的密码！' }, ({ getFieldValue }) => ({
                    validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致！'));
                    },
                }),]}
            >
                <Input.Password placeholder="确认密码" />
            </Form.Item>
            <Form.Item>
                <Button 
                    type="primary" 
                    htmlType="submit" 
                    disabled={loading}
                    style={{ width: '100%', height: '40px' }}
                >
                    注册
                </Button>
            </Form.Item>
            <div>
            注册登录即表示同意
            <a href="/terms.html" target="_blank">用户协议</a>
                    和
                    <a href="/privacy.html" target="_blank">隐私政策</a>
            </div>
        </Form>
    );
};

export default SignupForm;